import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  Target,
  Compass,
  Briefcase,
  Users,
  Sparkles,
  Home as HomeIcon,
  Hash,
  ListChecks,
  ArrowRight,
  ChevronRight,
  RefreshCcw,
  Download,
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import { useI18n } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { generateBlueprint } from "@/lib/blueprintEngine";
import {
  clearCommercialSession,
  generateCommercialPdf,
  getCommercialPdf,
  getCommercialReport,
  readCommercialAccessToken,
  readCommercialOrderId,
  validateCommercialPdfMetadata,
  validateCommercialReportResponse,
} from "@/lib/commercialAccess";
import ProductionDashboardV299 from "@/components/ProductionDashboardV299";

// B3-C3K-6B2B1 · production-default engine mode. Only an explicitly persisted
// "MOCK" opts into the legacy mock UI; missing/unknown resolves to V2.9.9.
const ENGINE_MODE_KEY = "lb.engineMode";
const ENGINE_MODE_MOCK = "MOCK";
const ENGINE_MODE_V299 = "V2.9.9";
function readEngineMode() {
  try {
    if (typeof localStorage === "undefined") return ENGINE_MODE_V299;
    const v = localStorage.getItem(ENGINE_MODE_KEY);
    return v === ENGINE_MODE_MOCK ? ENGINE_MODE_MOCK : ENGINE_MODE_V299;
  } catch (_) {
    return ENGINE_MODE_V299;
  }
}

const CARD_ICONS = {
  trait: Target,
  wealth: Compass,
  career: Briefcase,
  relationships: Users,
  growth: Sparkles,
  fengshui: HomeIcon,
  numbers: Hash,
  priority: ListChecks,
};

const DEFAULT_INPUT = {
  name: "张明",
  birthDate: "1990-05-12",
  birthTime: "辰时 (07:00-09:00)",
  gender: "男",
  region: "华东地区",
};

const SectionTitle = ({ eyebrow, title, testid }) => (
  <div className="mb-6" data-testid={testid}>
    <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
      {eyebrow}
    </div>
    <h2 className="mt-2 font-serif-cn text-2xl sm:text-3xl text-gold-100">{title}</h2>
    <div className="hairline-gold mt-4" />
  </div>
);

const SummaryCard = ({ id, data }) => {
  const Icon = CARD_ICONS[id] || Target;
  return (
    <div
      data-testid={`summary-card-${id}`}
      className="card-premium corner-frame rounded-2xl p-5 sm:p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-9 h-9 rounded-lg bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gold-300" strokeWidth={1.6} />
        </div>
        <span className="text-[10px] tracking-widest px-2 py-0.5 rounded-full border border-gold-500/30 text-gold-300/90">
          {data.status}
        </span>
      </div>
      <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80">
        {data.title}
      </div>
      <div className="mt-2 font-serif-cn text-gold-100 text-lg leading-snug">
        {data.value}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [openTheme, setOpenTheme] = useState(null);
  const [usingDefault, setUsingDefault] = useState(false);
  // V1 Task 2 Remediation · resolve engine mode SYNCHRONOUSLY from
  // storage during the first render so we never flash MOCK content
  // before switching to V2.9.9. Same-tick decision, no useEffect gap.
  const [engineMode] = useState(() => readEngineMode());
  const isV299 = engineMode === ENGINE_MODE_V299;
  // V1 Task 2 Remediation · initialize MOCK result synchronously so
  // SSR/first-paint has content and the loading state does not mask
  // the mock sections. In V2.9.9 mode we leave it null — production
  // Dashboard never reads `result`.
  const [result, setResult] = useState(() => {
    if (readEngineMode() !== ENGINE_MODE_MOCK) return null;
    try {
      if (typeof localStorage !== "undefined") {
        const cached = localStorage.getItem("lb.result");
        if (cached) return JSON.parse(cached);
      }
    } catch (_) { /* ignore */ }
    try { return generateBlueprint(DEFAULT_INPUT); } catch (_) { return null; }
  });
  const [v299Read, setV299Read] = useState(() =>
    engineMode === ENGINE_MODE_V299 ? { checking: true } : null
  );
  const [pdfDownload, setPdfDownload] = useState({
    busy: false,
    error: null,
  });
  // Truthful production-validation state: identity/status labels may only
  // claim VALIDATED_V299 when the server READY snapshot passed validation.
  const hasValidV299Report = isV299 && !!v299Read && v299Read.ok === true;

  // Track whether the initial MOCK result came from `lb.result` or
  // the DEFAULT_INPUT fallback (drives the "using default" notice).
  useEffect(() => {
    if (engineMode !== ENGINE_MODE_MOCK) { setUsingDefault(false); return; }
    let cached = null;
    try { cached = localStorage.getItem("lb.result"); } catch (_) { /* ignore */ }
    setUsingDefault(!cached);
  }, [engineMode]);

  // V2.9.9 consumes only the server-owned immutable READY snapshot.
  // Browser lb.result.v299 is intentionally ignored and cannot grant authority.
  useEffect(() => {
    if (engineMode === ENGINE_MODE_MOCK) {
      setV299Read(null);
      try {
        const cached = typeof localStorage !== "undefined" && localStorage.getItem("lb.result");
        if (cached) { setResult(JSON.parse(cached)); return undefined; }
      } catch (_) { /* ignore */ }
      try { setResult(generateBlueprint(DEFAULT_INPUT)); } catch (_) { /* ignore */ }
      return undefined;
    }

    let active = true;
    setResult(null);
    setV299Read({ checking: true });

    const orderId = readCommercialOrderId();
    const accessToken = readCommercialAccessToken();
    if (!orderId || !accessToken) {
      setV299Read({ ok: false, reason: "MISSING_AUTHORITY" });
      return () => { active = false; };
    }

    getCommercialReport(orderId, accessToken)
      .then((response) => {
        if (!active) return;
        setV299Read(validateCommercialReportResponse(response));
      })
      .catch((error) => {
        if (!active) return;
        if (error && error.status === 404) {
          clearCommercialSession();
          navigate("/checkout", { replace: true });
          return;
        }
        setV299Read({
          ok: false,
          reason: error && error.status === 409 ? "REPORT_NOT_READY" : "REPORT_UNAVAILABLE",
        });
      });

    return () => { active = false; };
  }, [engineMode, navigate]);

  const handlePdfDownload = async () => {
    if (!hasValidV299Report || pdfDownload.busy) return;

    const orderId = readCommercialOrderId();
    const accessToken = readCommercialAccessToken();
    if (!orderId || !accessToken) {
      clearCommercialSession();
      navigate("/checkout", { replace: true });
      return;
    }

    setPdfDownload({ busy: true, error: null });

    try {
      const metadata = await generateCommercialPdf(orderId, accessToken, locale);
      const validated = validateCommercialPdfMetadata(metadata);
      if (!validated.ok) throw new Error("invalid PDF metadata");

      const download = await getCommercialPdf(orderId, accessToken, locale);
      if (
        !download.blob ||
        download.blob.size !== metadata.size_bytes ||
        download.contentType !== "application/pdf" ||
        download.reportId !== metadata.report_id ||
        download.artifactDigest !== metadata.artifact_digest ||
        download.locale !== locale ||
        metadata.locale !== locale
      ) {
        throw new Error("invalid PDF artifact response");
      }

      const objectUrl = URL.createObjectURL(download.blob);
      try {
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = download.filename || "Life_Blueprint.pdf";
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }

      setPdfDownload({ busy: false, error: null });
    } catch (error) {
      if (error && error.status === 404) {
        clearCommercialSession();
        navigate("/checkout", { replace: true });
        return;
      }
      setPdfDownload({ busy: false, error: "DOWNLOAD_FAILED" });
    }
  };

  const summaryOrder = useMemo(
    () => ["trait", "wealth", "career", "relationships", "growth", "fengshui", "numbers", "priority"],
    []
  );

  // Loading gate: MOCK waits for its legacy result; production waits for
  // the authenticated server snapshot and never consults browser result data.
  if ((!isV299 && !result) || (isV299 && v299Read && v299Read.checking)) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center text-slate-300" data-testid="dashboard-loading">
        {t("dashboard.loading")}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-900 relative" data-testid="dashboard-page">
      <div className="pointer-events-none absolute inset-0 opacity-40 bg-watermark-compass" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.15] bg-grid" />

      <div className="relative z-10">
        <BrandHeader />

        <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 space-y-14">
          {/* Header */}
          <section className="fade-up" data-testid="dashboard-header">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
                  {t("dashboard.eyebrow")}
                </div>
                <h1 className="mt-2 font-serif-cn text-3xl sm:text-4xl lg:text-5xl text-gold-100">
                  {t("dashboard.title")}
                </h1>
                <div
                  className="mt-2 text-sm text-slate-300"
                  data-testid="dashboard-header-profile"
                >
                  {isV299 ? (
                    // V2.9.9 header reads ONLY the stored, validated production
                    // report profile. Never reads mock `result.*`.
                    <>
                      {t("dashboard.profileOverview")} ·{" "}
                      <span className="text-emerald-200" data-testid="dashboard-v299-profile-name">
                        {(v299Read && v299Read.ok && v299Read.report.profile && v299Read.report.profile.name) || "—"}
                      </span>
                      {v299Read && v299Read.ok && v299Read.report.profile && v299Read.report.profile.birthDateISO && (
                        <> · <span data-testid="dashboard-v299-profile-birth">{v299Read.report.profile.birthDateISO}</span></>
                      )}
                      <span
                        data-testid="dashboard-v299-engine-chip"
                        className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-emerald-400/50 text-emerald-200 font-mono tracking-widest"
                      >
                        {hasValidV299Report ? t("dashboard.reportReady") : t("dashboard.reportUnavailable")}
                      </span>
                    </>
                  ) : (
                    <>
                      {t("dashboard.profileOverview")} ·{" "}
                      <span className="text-gold-200" data-testid="dashboard-mock-profile-name">
                        {result.profile.name || t("dashboard.sampleUser")}
                      </span>
                      {result.profile.gender && (
                        <> · <span data-testid="dashboard-mock-profile-gender">{result.profile.gender}</span></>
                      )}
                      {result.profile.region && (
                        <> · <span data-testid="dashboard-mock-profile-region">{result.profile.region}</span></>
                      )}
                      <span
                        data-testid="dashboard-mock-variant-chip"
                        className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-gold-500/40 text-gold-300 font-mono tracking-widest"
                      >
                        {t("dashboard.profileLabel")} {result.variantId} · {result.variantLabel}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  data-testid="poc-badge"
                  className={
                    "text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border font-mono " +
                    (isV299
                      ? "border-emerald-400/50 text-emerald-200 bg-emerald-500/10"
                      : "border-gold-500/40 text-gold-200 bg-gold-500/5")
                  }
                >
                  {isV299
                    ? (hasValidV299Report ? t("dashboard.reportReady") : t("dashboard.reportUnavailable"))
                    : t("dashboard.sampleResult")}
                </span>
                {hasValidV299Report ? (
                  <button
                    type="button"
                    data-testid="download-pdf-button"
                    onClick={handlePdfDownload}
                    disabled={pdfDownload.busy}
                    className="btn-gold rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {pdfDownload.busy
                      ? t("dashboard.pdfPreparing")
                      : t("dashboard.downloadPdf")}
                  </button>
                ) : null}
                <button
                  type="button"
                  data-testid="edit-profile-button"
                  onClick={() => navigate("/form")}
                  className="btn-ghost-gold rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  {t("dashboard.editProfile")}
                </button>
              </div>
            </div>

            {pdfDownload.error ? (
              <div
                data-testid="pdf-download-error"
                className="mt-4 rounded-xl border border-rose-500/35 bg-rose-950/25 px-4 py-3 text-xs text-rose-100"
              >
                {t("dashboard.pdfDownloadFailed")}
              </div>
            ) : null}

            {usingDefault && !isV299 && (
              <div
                data-testid="dashboard-default-notice"
                className="mt-6 rounded-xl border border-gold-500/25 bg-navy-800/60 p-4 flex items-start gap-3"
              >
                <ShieldAlert className="w-4 h-4 text-gold-400/90 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">
                  {t("dashboard.defaultNoticeBefore")}{" "}
                  <button
                    className="text-gold-200 underline underline-offset-2"
                    onClick={() => navigate("/form")}
                    data-testid="default-notice-link"
                  >
                    {t("dashboard.defaultNoticeLink")}
                  </button>{" "}
                  {t("dashboard.defaultNoticeAfter")}
                </p>
              </div>
            )}
          </section>

          {/* Mock-driven sections — rendered ONLY in MOCK mode. In V2.9.9
              mode the Dashboard consumes the STORED authoritative report. */}
          {!isV299 ? (
            <>
              {/* Summary cards */}
              <section data-testid="summary-section">
            <SectionTitle eyebrow="Section 01" title={t("dashboard.summaryTitle")} testid="section-title-summary" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {summaryOrder.map((id) => (
                <SummaryCard key={id} id={id} data={result.summaryCards[id]} />
              ))}
            </div>
          </section>

          {/* Blueprint overview - metric bars */}
          <section data-testid="overview-section">
            <SectionTitle eyebrow="Section 02" title={t("dashboard.overviewTitle")} testid="section-title-overview" />
            <div className="card-premium rounded-2xl p-6 sm:p-8">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                {result.overviewMetrics.map((m) => (
                  <div key={m.key} data-testid={`overview-metric-${m.key}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="font-serif-cn text-gold-100 text-base">{m.label}</span>
                      <span className="text-xs font-mono tracking-widest text-gold-300">
                        {m.value}%
                      </span>
                    </div>
                    <Progress
                      value={m.value}
                      className="h-1.5 bg-navy-700 [&>div]:bg-gradient-to-r [&>div]:from-[#F3E5AB] [&>div]:to-[#D4AF37]"
                    />
                  </div>
                ))}
              </div>
              <div className="hairline-gold my-6" />
              <p className="text-xs text-slate-400 leading-relaxed">
                {t("dashboard.overviewNote")}
              </p>
            </div>
          </section>

          {/* 12 Theme cards */}
          <section data-testid="themes-section">
            <SectionTitle eyebrow="Section 03" title={t("dashboard.themesTitle")} testid="section-title-themes" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {result.themes.map((theme) => (
                <button
                  key={theme.id}
                  data-testid={`theme-card-${theme.id}`}
                  onClick={() => setOpenTheme(theme)}
                  className="card-premium corner-frame rounded-2xl p-5 text-left group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono tracking-[0.32em] uppercase text-gold-400/80">
                      {theme.code}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gold-400/70 group-hover:translate-x-0.5 transition" />
                  </div>
                  <div className="mt-3 font-serif-cn text-gold-100 text-base">{theme.title}</div>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed line-clamp-2">
                    {theme.summary}
                  </p>
                  <div className="mt-4">
                    <div className="flex justify-between text-[10px] font-mono tracking-widest text-gold-400/70 mb-1">
                      <span>{t("dashboard.strength")}</span>
                      <span>{theme.strength}</span>
                    </div>
                    <div className="h-1 rounded-full bg-navy-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37]"
                        style={{ width: `${theme.strength}%` }}
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* 24 numbers */}
          <section data-testid="numbers-section">
            <SectionTitle eyebrow="Section 04" title={t("dashboard.numbersTitle")} testid="section-title-numbers" />
            <div className="grid md:grid-cols-2 gap-5">
              <div className="card-premium rounded-2xl p-6" data-testid="favorable-numbers">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-4">
                  {t("dashboard.favorableNumbers")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.numbers.favorable.map((n) => (
                    <span
                      key={`f-${n}`}
                      data-testid={`favorable-num-${n}`}
                      className="min-w-[44px] text-center px-3 py-2 rounded-lg border border-gold-500/45 bg-gold-500/10 text-gold-100 font-mono tracking-widest text-sm"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
              <div className="card-premium rounded-2xl p-6" data-testid="unfavorable-numbers">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-4">
                  {t("dashboard.cautionaryNumbers")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.numbers.unfavorable.map((n) => (
                    <span
                      key={`u-${n}`}
                      data-testid={`unfavorable-num-${n}`}
                      className="min-w-[44px] text-center px-3 py-2 rounded-lg border border-slate-500/40 bg-navy-700/60 text-slate-300 font-mono tracking-widest text-sm"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 card-premium rounded-2xl p-6" data-testid="numbers-usage">
              <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
                {t("dashboard.usage")}
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{result.numbers.usage}</p>
              <p className="mt-3 text-xs text-slate-500">
                * {t("dashboard.numbersNote")}
              </p>
            </div>
          </section>

          {/* Wealth blueprint 4 quadrants */}
          <section data-testid="wealth-section">
            <SectionTitle
              eyebrow="Section 05"
              title={t("dashboard.wealthTitle")}
              testid="section-title-wealth"
            />
            <div className="grid md:grid-cols-2 gap-5">
              <div className="card-premium corner-frame rounded-2xl p-6" data-testid="wealth-focus">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
                  {t("dashboard.wealthFocus")}
                </div>
                <p className="text-sm text-slate-200 leading-relaxed">{result.wealth.focus}</p>
              </div>
              <div className="card-premium corner-frame rounded-2xl p-6" data-testid="wealth-priority">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
                  {t("dashboard.wealthPriority")}
                </div>
                <ul className="space-y-2 text-sm text-slate-200">
                  {result.wealth.priority.map((p, i) => (
                    <li key={i} className="flex gap-2">
                      <ArrowRight className="w-3.5 h-3.5 text-gold-300 mt-1 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-premium corner-frame rounded-2xl p-6" data-testid="wealth-watch">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
                  {t("dashboard.wealthWatch")}
                </div>
                <ul className="space-y-2 text-sm text-slate-200">
                  {result.wealth.watch.map((w, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-gold-300 mt-1">·</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-premium corner-frame rounded-2xl p-6" data-testid="wealth-next">
                <div className="text-xs tracking-widest uppercase font-mono text-gold-400/80 mb-3">
                  {t("dashboard.wealthNext")}
                </div>
                <ol className="space-y-2 text-sm text-slate-200">
                  {result.wealth.next.map((n, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[10px] font-mono tracking-widest text-gold-300 mt-1">
                        0{i + 1}
                      </span>
                      <span>{n}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section data-testid="dashboard-disclaimer" className="pt-2">
            <div className="rounded-2xl border border-gold-500/25 bg-navy-800/50 p-6 flex items-start gap-3">
              <ShieldAlert className="w-4 h-4 text-gold-400/90 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">
                {result.disclaimer}
              </p>
            </div>
          </section>
            </>
          ) : v299Read && v299Read.ok ? (
            <ProductionDashboardV299
              report={v299Read.report}
              onEdit={() => navigate("/form")}
            />
          ) : (
            <section
              data-testid="v299-failclosed"
              className="rounded-2xl border border-rose-500/40 bg-rose-950/30 p-8 text-center"
            >
              <div className="text-xs tracking-widest uppercase font-mono text-rose-200/90 mb-3">
                {t("dashboard.failclosedTitle")}
              </div>
              <p
                className="text-sm text-slate-200 leading-relaxed max-w-xl mx-auto"
                data-testid="v299-failclosed-message"
              >
                {t("dashboard.failclosedMessage")}
              </p>
              <button
                type="button"
                data-testid="v299-failclosed-form-button"
                onClick={() => navigate("/form")}
                className="btn-gold rounded-full px-6 py-3 text-sm mt-5 inline-flex items-center gap-2"
              >
                {t("dashboard.failclosedButton")} <ArrowRight className="w-4 h-4" />
              </button>
            </section>
          )}
        </main>

        <footer className="border-t border-gold-500/15 py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-slate-400">
              {isV299 ? t("dashboard.footerBrand") : t("dashboard.footerBrandSample")}
            </div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
              {isV299
                ? t("dashboard.footerProductionNote")
                : t("dashboard.footerSampleNote")}
            </div>
          </div>
        </footer>
      </div>

      {/* Theme detail dialog */}
      <Dialog open={!!openTheme} onOpenChange={(o) => !o && setOpenTheme(null)}>
        <DialogContent
          data-testid="theme-dialog"
          className="bg-navy-800 border border-gold-500/35 text-gold-100 max-w-lg"
        >
          {openTheme && (
            <>
              <DialogHeader>
                <div className="text-[10px] font-mono tracking-[0.35em] uppercase text-gold-400/80">
                  {openTheme.code} · {t("dashboard.themeDetail")}
                </div>
                <DialogTitle className="font-serif-cn text-gold-100 text-2xl mt-2">
                  {openTheme.title}
                </DialogTitle>
                <DialogDescription className="text-slate-300 text-sm leading-relaxed">
                  {openTheme.summary}
                </DialogDescription>
              </DialogHeader>
              <div className="hairline-gold my-2" />
              <div>
                <div className="flex justify-between text-[10px] font-mono tracking-widest text-gold-400/80 mb-1">
                  <span>{t("dashboard.energyStrength")}</span>
                  <span>{openTheme.strength} / 100</span>
                </div>
                <div className="h-1.5 rounded-full bg-navy-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37]"
                    style={{ width: `${openTheme.strength}%` }}
                  />
                </div>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed mt-4">{openTheme.detail}</p>
              <p className="text-[11px] text-slate-500 mt-4">
                * {t("dashboard.themeSampleNote")}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
